/** Branches and BIR invoice series (M01-04, M01-05, CAS-01–08). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useBranches, type Branch } from '../../lib/queries';
import { ReasonDialog } from '../../components/ReasonDialog';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';
import { SeriesUsage } from '../../components/SeriesUsage';

interface Series {
  id: string;
  branchId: string;
  branch: Branch;
  docType: string;
  mode: 'SYSTEM' | 'MANUAL_ATP';
  prefix: string;
  rangeStart: number;
  rangeEnd: number;
  nextNumber: number;
  firstIssued: string | null;
  status: 'ACTIVE' | 'CLOSED';
  closedReason: string | null;
}

export const DOC_TYPES: Record<string, string> = {
  SALES_INVOICE: 'Sales Invoice',
  CREDIT_MEMO: 'Credit Memo',
  DEBIT_MEMO: 'Debit Memo',
  OFFICIAL_RECEIPT: 'Official Receipt',
  COLLECTION_RECEIPT: 'Collection Receipt',
  DELIVERY_RECEIPT: 'Delivery Receipt',
};

export function BranchesPage(): JSX.Element {
  const { can } = useAuth();
  const branches = useBranches();
  const series = useQuery({ queryKey: ['series'], queryFn: () => api<Series[]>('/company/document-series') });
  const queryClient = useQueryClient();
  const [branchDialog, setBranchDialog] = useState<Branch | 'new' | null>(null);
  const [seriesDialog, setSeriesDialog] = useState(false);
  const [closing, setClosing] = useState<Series | null>(null);
  const [error, setError] = useState<string | null>(null);
  const manage = can('company.manage');

  const close = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api(`/company/document-series/${id}/close`, { method: 'POST', body: { reason } }),
    onSuccess: () => {
      setClosing(null);
      void queryClient.invalidateQueries({ queryKey: ['series'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not close.'),
  });

  return (
    <>
      <PageHeader title="Branches & Invoice Series" description="Each BIR-registered branch has its own serial ranges per document type. A range freezes once it issues its first document." />
      <Card
        title="Branches"
        className="mb-4"
        actions={manage && <Button size="sm" onClick={() => setBranchDialog('new')}><Plus aria-hidden="true" className="h-4 w-4" /> Branch</Button>}
      >
        {branches.isLoading && <Skeleton className="h-24 w-full" />}
        {branches.data && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Address</Th>
                <Th>RDO</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {branches.data.map((b) => (
                <tr key={b.id} className={b.active ? '' : 'opacity-50'}>
                  <Td className="tabular-nums">{b.code}</Td>
                  <Td className="font-medium">{b.name} {b.isHeadOffice && <StatusChip tone="info">Head office</StatusChip>}</Td>
                  <Td className="text-slate-500">{[b.street, b.barangay, b.city, b.province, b.zip].filter(Boolean).join(', ') || '—'}</Td>
                  <Td>{b.rdoCode || '—'}</Td>
                  <Td align="right">
                    {manage && (
                      <button className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label={`Edit ${b.name}`} onClick={() => setBranchDialog(b)}>
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <Card
        title="Invoice series"
        description="System series are numbered by CoreX-lite at issuance, with no gaps. Manual (ATP) series record the numbers on pre-printed booklets."
        actions={manage && <Button size="sm" onClick={() => setSeriesDialog(true)}><Plus aria-hidden="true" className="h-4 w-4" /> Series</Button>}
      >
        {error && <div className="mb-3"><Alert tone="critical">{error}</Alert></div>}
        {series.isLoading && <Skeleton className="h-24 w-full" />}
        {series.data?.length === 0 && <EmptyState title="No series yet" description="Add a Sales Invoice series for the head office to start invoicing." />}
        {series.data && series.data.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Branch</Th>
                <Th>Document</Th>
                <Th>Mode</Th>
                <Th>Range</Th>
                <Th>Next</Th>
                <Th>Used</Th>
                <Th>Status</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {series.data.map((s) => {
                const size = s.rangeEnd - s.rangeStart + 1;
                const used = s.nextNumber - s.rangeStart;
                const pct = Math.round((used / size) * 100);
                return (
                  <tr key={s.id} className={s.status === 'CLOSED' ? 'opacity-60' : ''}>
                    <Td className="whitespace-nowrap">{s.branch.code} {s.branch.name}</Td>
                    <Td className="whitespace-nowrap">{DOC_TYPES[s.docType] ?? s.docType}</Td>
                    <Td>{s.mode === 'SYSTEM' ? 'System (CAS)' : 'Manual (ATP)'}</Td>
                    <Td className="whitespace-nowrap tabular-nums">{s.prefix}{s.rangeStart} – {s.prefix}{s.rangeEnd}</Td>
                    <Td className="tabular-nums">{s.nextNumber > s.rangeEnd ? '—' : `${s.prefix}${s.nextNumber}`}</Td>
                    <Td><StatusChip tone={pct >= 80 ? 'warning' : 'neutral'}>{pct}%</StatusChip></Td>
                    <Td><StatusChip tone={s.status === 'ACTIVE' ? 'ok' : 'neutral'}>{s.status === 'ACTIVE' ? 'Active' : 'Closed'}</StatusChip></Td>
                    <Td align="right">{manage && s.status === 'ACTIVE' && <Button size="sm" variant="ghost" onClick={() => setClosing(s)}>Close</Button>}</Td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Card>

      {branchDialog && <BranchDialog branch={branchDialog === 'new' ? null : branchDialog} onClose={() => setBranchDialog(null)} />}
      {seriesDialog && <SeriesDialog branches={(branches.data ?? []).filter((b) => b.active)} onClose={() => setSeriesDialog(false)} />}
      {closing && (
        <ReasonDialog
          title={`Close ${DOC_TYPES[closing.docType]} ${closing.prefix}${closing.rangeStart}–${closing.rangeEnd}?`}
          description="Closing is final. Unused numbers are reported for surrender to the BIR; open a new range to continue."
          confirmLabel="Close series"
          busy={close.isPending}
          error={error}
          onClose={() => { setClosing(null); setError(null); }}
          onConfirm={(reason) => close.mutate({ id: closing.id, reason })}
        />
      )}
      <SeriesUsage />
    </>
  );
}

function BranchDialog({ branch, onClose }: { branch: Branch | null; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: branch?.code ?? '',
    name: branch?.name ?? '',
    street: branch?.street ?? '',
    barangay: branch?.barangay ?? '',
    city: branch?.city ?? '',
    province: branch?.province ?? '',
    zip: branch?.zip ?? '',
    rdoCode: branch?.rdoCode ?? '',
    active: branch?.active ?? true,
  });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      if (branch) return api(`/company/branches/${branch.id}`, { method: 'PATCH', body: form });
      const { active: _a, ...create } = form;
      void _a;
      return api('/company/branches', { method: 'POST', body: create });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not save.'),
  });
  const input = (key: keyof typeof form, text: string, required = false) => (
    <Field label={text} required={required}>
      <input className={inputClass} value={form[key] as string} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
    </Field>
  );
  return (
    <Modal title={branch ? `Edit ${branch.name}` : 'New branch'} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
          {input('code', 'Branch code', true)}
          {input('name', 'Name', true)}
        </div>
        {input('street', 'Street')}
        <div className="grid gap-4 sm:grid-cols-2">
          {input('barangay', 'Barangay')}
          {input('city', 'City / municipality')}
          {input('province', 'Province')}
          {input('zip', 'ZIP')}
          {input('rdoCode', 'RDO code')}
        </div>
        {branch && !branch.isHeadOffice && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Active
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function SeriesDialog({ branches, onClose }: { branches: Branch[]; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ branchId: branches[0]?.id ?? '', docType: 'SALES_INVOICE', mode: 'SYSTEM', prefix: '', rangeStart: '1', rangeEnd: '10000' });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api('/company/document-series', { method: 'POST', body: { ...form, rangeStart: Number(form.rangeStart), rangeEnd: Number(form.rangeEnd) } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['series'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not save.'),
  });
  const set = (patch: Partial<typeof form>): void => setForm((f) => ({ ...f, ...patch }));
  return (
    <Modal title="New invoice series" description="Use the exact range registered with the BIR." onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Branch" required>
            <select className={inputClass} value={form.branchId} onChange={(e) => set({ branchId: e.target.value })}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.code} {b.name}</option>)}
            </select>
          </Field>
          <Field label="Document" required>
            <select className={inputClass} value={form.docType} onChange={(e) => set({ docType: e.target.value })}>
              {Object.entries(DOC_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="Mode" required>
            <select className={inputClass} value={form.mode} onChange={(e) => set({ mode: e.target.value })}>
              <option value="SYSTEM">System-generated (CAS)</option>
              <option value="MANUAL_ATP">Pre-printed booklets (ATP)</option>
            </select>
          </Field>
          <Field label="Prefix" hint="Optional, e.g. SI-">
            <input className={inputClass} maxLength={10} value={form.prefix} onChange={(e) => set({ prefix: e.target.value })} />
          </Field>
          <Field label="First number" required>
            <input className={inputClass} inputMode="numeric" value={form.rangeStart} onChange={(e) => set({ rangeStart: e.target.value.replace(/\D/g, '') })} />
          </Field>
          <Field label="Last number" required>
            <input className={inputClass} inputMode="numeric" value={form.rangeEnd} onChange={(e) => set({ rangeEnd: e.target.value.replace(/\D/g, '') })} />
          </Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
